import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { CheckCircle, AlertCircle, Loader, DollarSign, Send } from 'lucide-react';

const ConfirmPayment = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [invoiceData, setInvoiceData] = useState(null);
  
  const [formData, setFormData] = useState({
    paymentMethod: '',
    amount: '',
    reference: '',
    note: '',
  });

  // API base URL for public endpoints
  const API_BASE_URL = (import.meta.env.VITE_API_URL || '').replace('/api', '');

  useEffect(() => {
    loadInvoiceData();
  }, [token]);

  const loadInvoiceData = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_BASE_URL}/api/public/payment/confirm/${token}`);
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to load invoice');
        return;
      }
      
      setInvoiceData(data);
      setFormData(prev => ({
        ...prev,
        amount: data.amountDue?.toString() || '',
      }));
    } catch (err) {
      setError('Failed to load invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.paymentMethod) {
      setError('Please select a payment method');
      return;
    }
    
    try {
      setSubmitting(true);
      setError(null);
      
      const response = await fetch(`${API_BASE_URL}/api/public/payment/confirm/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          paymentMethod: formData.paymentMethod,
          amount: formData.amount ? parseFloat(formData.amount) : null,
          reference: formData.reference || null,
          note: formData.note || null,
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        setError(data.error || 'Failed to submit confirmation');
        return;
      }
      
      setSuccess(true);
      setInvoiceData(prev => ({ ...prev, successData: data }));
    } catch (err) {
      setError('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading invoice...</p>
        </div>
      </div>
    );
  }

  // Error state (no invoice found)
  if (error && !invoiceData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-gray-900 mb-2">Link Invalid</h1>
          <p className="text-gray-600">{error}</p>
        </div>
      </div>
    );
  }

  // Already paid state
  if (invoiceData?.alreadyPaid) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Already Paid!</h1>
          <p className="text-gray-600 mb-4">
            Invoice #{invoiceData.invoiceNumber} has already been marked as paid.
          </p>
          <p className="text-sm text-gray-500">
            Thank you for your business with {invoiceData.businessName}!
          </p>
        </div>
      </div>
    );
  }

  // Success state
  if (success) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full text-center">
          <div 
            className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
            style={{ backgroundColor: `${invoiceData.brandColor}20` }}
          >
            <CheckCircle className="w-10 h-10" style={{ color: invoiceData.brandColor }} />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Thank You!</h1>
          <p className="text-gray-600 mb-6">
            We've received your payment confirmation for Invoice #{invoiceData.invoiceNumber}.
            {invoiceData.businessName} will update your account shortly.
          </p>
          <div className="bg-gray-50 rounded-lg p-4 text-left">
            <p className="text-sm text-gray-600 mb-1">Questions? Contact:</p>
            <p className="font-medium text-gray-900">{invoiceData.businessName}</p>
            {invoiceData.successData?.businessPhone && (
              <p className="text-sm text-gray-600">{invoiceData.successData.businessPhone}</p>
            )}
            {invoiceData.successData?.businessEmail && (
              <p className="text-sm text-gray-600">{invoiceData.successData.businessEmail}</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Has pending confirmation already
  if (invoiceData?.hasPendingConfirmation) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-lg p-8 max-w-md w-full">
          <div className="text-center mb-6">
            <div 
              className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: '#fef3c7' }}
            >
              <AlertCircle className="w-10 h-10 text-amber-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900 mb-2">Confirmation Pending</h1>
            <p className="text-gray-600">
              You've already submitted a payment confirmation for this invoice.
            </p>
          </div>
          
          <div className="bg-amber-50 rounded-lg p-4 mb-6">
            <p className="text-sm text-amber-800">
              <strong>Submitted:</strong> {new Date(invoiceData.pendingConfirmation.submittedAt).toLocaleDateString()}
            </p>
            <p className="text-sm text-amber-800">
              <strong>Method:</strong> {invoiceData.pendingConfirmation.paymentMethod}
            </p>
            <p className="text-sm text-amber-800">
              <strong>Amount:</strong> {formatCurrency(invoiceData.pendingConfirmation.amount)}
            </p>
          </div>
          
          <p className="text-sm text-gray-500 text-center mb-4">
            Need to update your submission? You can submit again below.
          </p>
          
          <button
            onClick={() => setInvoiceData(prev => ({ ...prev, hasPendingConfirmation: false }))}
            className="w-full py-3 px-4 bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium rounded-lg transition-colors"
          >
            Update Payment Confirmation
          </button>
        </div>
      </div>
    );
  }

  // Main form
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          {invoiceData.businessLogo ? (
            <img 
              src={invoiceData.businessLogo} 
              alt={invoiceData.businessName}
              className="h-12 mx-auto mb-3"
            />
          ) : (
            <div 
              className="w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-3"
              style={{ backgroundColor: invoiceData.brandColor }}
            >
              <DollarSign className="w-6 h-6 text-white" />
            </div>
          )}
          <h1 className="text-2xl font-bold text-gray-900">{invoiceData.businessName}</h1>
          <p className="text-gray-600">Payment Confirmation</p>
        </div>

        {/* Invoice Summary */}
        <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
          <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
            <div>
              <p className="text-sm text-gray-500">Invoice</p>
              <p className="font-semibold text-gray-900">#{invoiceData.invoiceNumber}</p>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-500">Amount Due</p>
              <p className="text-2xl font-bold" style={{ color: invoiceData.brandColor }}>
                {formatCurrency(invoiceData.amountDue)}
              </p>
            </div>
          </div>
          
          {invoiceData.customerName && (
            <p className="text-sm text-gray-600">
              <span className="text-gray-500">Customer:</span> {invoiceData.customerName}
            </p>
          )}
          {invoiceData.dueDate && (
            <p className="text-sm text-gray-600">
              <span className="text-gray-500">Due Date:</span> {new Date(invoiceData.dueDate).toLocaleDateString()}
            </p>
          )}
        </div>

        {/* Confirmation Form */}
        <div className="bg-white rounded-xl shadow-lg p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            Confirm Your Payment
          </h2>
          <p className="text-sm text-gray-600 mb-6">
            Let us know how you paid so we can update your account.
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-700">{error}</p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Payment Method */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                How did you pay? *
              </label>
              <div className="grid grid-cols-2 gap-2">
                {invoiceData.paymentMethods?.map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, paymentMethod: method.value })}
                    className={`p-3 rounded-lg border-2 text-left transition-all ${
                      formData.paymentMethod === method.value
                        ? 'border-blue-500 bg-blue-50'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <p className="font-medium text-gray-900">{method.label}</p>
                    {method.hint && (
                      <p className="text-xs text-gray-500 truncate">{method.hint}</p>
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Amount */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Amount Paid
              </label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">$</span>
                <input
                  type="number"
                  step="0.01"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                  className="w-full pl-8 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder={invoiceData.amountDue?.toString()}
                />
              </div>
            </div>

            {/* Reference */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reference / Confirmation
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <input
                type="text"
                value={formData.reference}
                onChange={(e) => setFormData({ ...formData, reference: e.target.value })}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Venmo @username, Zelle confirmation #, Check #..."
              />
            </div>

            {/* Note */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Note
                <span className="text-gray-400 font-normal ml-1">(optional)</span>
              </label>
              <textarea
                value={formData.note}
                onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                rows="2"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Any additional details..."
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={submitting || !formData.paymentMethod}
              className="w-full py-3 px-4 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ backgroundColor: invoiceData.brandColor }}
            >
              {submitting ? (
                <>
                  <Loader className="w-5 h-5 animate-spin" />
                  Submitting...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5" />
                  Confirm Payment
                </>
              )}
            </button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-gray-500 mt-6">
          Questions? Contact {invoiceData.businessName}
        </p>
      </div>
    </div>
  );
};

export default ConfirmPayment;